<script lang="ts">
import type { Component as VueComponent } from 'vue';

export default {
  name: 'MenuDropdown',
};

export interface MenuItem {
  label: string;
  handler: (...params: any[]) => void;
  icon?: VueComponent;
  /** Tailwind text color class for the label (e.g. text-black, text-error). Defaults to text-black. */
  textColorClass?: string;
  /** Tailwind text color class for the icon (e.g. text-black, text-error). Defaults to text-secondary. */
  iconColorClass?: string;
  /** When true, item is visually disabled and handler is not invoked. */
  disabled?: boolean;
  /** Optional data-testid for the list item anchor. */
  testId?: string;
}
</script>

<script setup lang="ts">
import type { Component } from 'vue';
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { Menu } from '@lucide/vue';
import { useHeaderOffset } from '@/composables/useHeaderOffset';
import { useAnchoredMenuPosition } from '@/composables/useAnchoredMenuPosition';

const { totalOffset } = useHeaderOffset();

const props = withDefaults(
  defineProps<{
    items: MenuItem[];
    /** Items rendered at the top of the menu with the same style as items; a separator is shown below when present. */
    prependItems?: MenuItem[];
    /** Label of the currently selected item; when the matching item has an icon, it is shown on the trigger. */
    selectedLabel?: string;
    /** Fallback icon for the trigger when no selected item or selected item has no icon. When omitted, Menu is used. */
    icon?: Component;
    /** Align menu with the start (left) of the trigger. Default is end (right-aligned). */
    start?: boolean;
    end?: boolean;
    /** Menu opens above the trigger. */
    top?: boolean;
    /** Menu opens below the trigger (default). */
    bottom?: boolean;
    /** Menu opens to the left of the trigger. */
    left?: boolean;
    /** Menu opens to the right of the trigger. */
    right?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  }>(),
  { size: 'md' },
);

const selectedItem = computed(() => {
  if (!props.selectedLabel) return undefined;
  return props.items.find((i) => i.label === props.selectedLabel) ?? props.prependItems?.find((i) => i.label === props.selectedLabel);
});

const triggerIcon = computed(() => {
  const item = selectedItem.value;
  if (item?.icon) return item.icon;
  return props.icon ?? Menu;
});

const menuOpen = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const listRef = ref<HTMLUListElement | null>(null);
const { menuStyle, updateMenuPosition } = useAnchoredMenuPosition(triggerRef, listRef, props, { capHeight: true });

const menuItemSizeClass = computed(() => {
  const map = {
    xs: 'px-3 py-1.5 text-[0.6875rem]',
    sm: 'px-3.5 py-2 text-xs',
    md: 'px-3 py-2 text-sm',
    lg: 'px-5 py-2.5 text-sm',
    xl: 'px-6 py-2.5 text-base',
  } as const;
  return map[props.size];
});

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}

function closeMenu() {
  menuOpen.value = false;
}

function handleClickOutside(e: MouseEvent) {
  const target = e.target as Node;
  if (triggerRef.value?.contains(target) || listRef.value?.contains(target)) return;
  closeMenu();
}

function handleClick(item?: MenuItem) {
  listRef.value?.blur();
  item?.handler(item.label);
  closeMenu();
}

let clickOutsideCleanup: (() => void) | null = null;

watch(menuOpen, (value) => {
  if (!value) {
    // Keep menuStyle so the list stays put while the leave transition plays.
    clickOutsideCleanup?.();
    clickOutsideCleanup = null;
    return;
  }
  nextTick(() => {
    updateMenuPosition();
    const onScroll = (e: Event) => {
      if (listRef.value?.contains(e.target as Node)) return;
      const rect = triggerRef.value?.getBoundingClientRect();
      if (!rect) return;
      if (rect.top < totalOffset.value || rect.top >= window.innerHeight) {
        closeMenu();
        return;
      }
      updateMenuPosition();
    };
    clickOutsideCleanup = () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('scroll', onScroll, true);
    };
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('scroll', onScroll, true);
  });
});

onUnmounted(() => {
  clickOutsideCleanup?.();
});
</script>

<template>
  <div class="relative inline-block">
    <div
      ref="triggerRef"
      class="inline-block cursor-pointer"
      @click.stop="toggleMenu">
      <slot
        name="action"
        :trigger-icon="triggerIcon"
        :selected-label="selectedLabel">
        <label
          tabindex="0"
          class="inline-flex cursor-pointer items-center gap-1.5 rounded border border-gray-300 bg-white px-1 py-1 text-xs font-medium hover:bg-gray-50">
          <component
            :is="triggerIcon"
            class="h-4 w-4 shrink-0" />
          <span
            v-if="selectedLabel"
            class="font-medium"
            >{{ selectedLabel }}</span
          >
        </label>
      </slot>
    </div>

    <Teleport to="body">
      <Transition name="menu-fade">
        <ul
          v-if="menuOpen"
          ref="listRef"
          tabindex="0"
          class="fixed z-9999 flex min-w-max flex-col rounded-md border border-gray-200 bg-white px-1 py-1 shadow-lg"
          :class="menuStyle?.maxHeight ? 'overscroll-contain overflow-y-auto' : ''"
          :style="menuStyle || undefined"
          @click.stop>
          <template v-if="prependItems?.length">
            <li
              v-for="item of prependItems"
              :key="item.label">
              <a
                class="flex cursor-pointer items-center gap-2 rounded text-left transition-colors hover:bg-gray-200"
                :class="[menuItemSizeClass, item.textColorClass ?? 'text-black', item.disabled ? 'pointer-events-none opacity-50' : '']"
                :data-testid="item.testId"
                @click="
                  () => {
                    if (!item.disabled) handleClick(item);
                  }
                ">
                <component
                  v-if="item.icon"
                  :is="item.icon"
                  :class="['h-4 w-4 shrink-0', item.iconColorClass ?? 'text-secondary']" />
                {{ item.label }}
              </a>
            </li>
          </template>
          <hr
            v-if="prependItems?.length && items.length"
            class="-mx-1 my-1 border-gray-200" />
          <slot name="prepend" />
          <slot
            name="items"
            :items="items"
            :handleClick="handleClick">
            <li
              v-for="item of items"
              :key="item.label">
              <a
                class="flex cursor-pointer items-center gap-2 rounded text-left transition-colors hover:bg-gray-200"
                :class="[menuItemSizeClass, item.textColorClass ?? 'text-black', item.disabled ? 'pointer-events-none opacity-50' : '']"
                :data-testid="item.testId"
                @click="
                  () => {
                    if (!item.disabled) handleClick(item);
                  }
                ">
                <component
                  v-if="item.icon"
                  :is="item.icon"
                  :class="['h-4 w-4 shrink-0', item.iconColorClass ?? 'text-secondary']" />
                {{ item.label }}
              </a>
            </li>
          </slot>
        </ul>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
/* No entrance animation — the menu is anchored to the click, so it should be there on click.
   Only the dismissal fades, which needs no enter-side rules for Vue to skip the enter. */
.menu-fade-leave-active {
  transition: opacity 0.12s ease-out;
}

.menu-fade-leave-to {
  opacity: 0;
}
</style>
